// Passkeys in the browser. The browser's own passkey prompt does the work; what goes
// in and out of it is the plain WebAuthn JSON that the Halo API's passkey
// endpoints and Cognito passkey sign-in exchange, which @simplewebauthn/browser
// converts to and from the browser's binary form.
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';

import * as passkeyApi from 'darwin_core/api/passkeys';
import { passkeyName, registrationRefused } from 'darwin_core/passkeys';

export function passkeysSupported() {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

// The user closed the browser's prompt, it timed out, or the browser had no
// passkey to offer: the browser deliberately reports these the same way, so
// none of them is an error worth reporting as a failure.
export function passkeyCancelled(error) {
  return (
    error?.name === 'NotAllowedError' ||
    error?.name === 'AbortError' ||
    error?.code === 'ERROR_CEREMONY_ABORTED'
  );
}

// This browser already holds a passkey for the account (the API lists the
// account's passkeys in the registration options so it can tell).
export function passkeyAlreadyAdded(error) {
  return error?.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';
}

// Most specific first: Edge and Opera also claim Chrome, every iOS browser and
// Chrome claim Safari, iPhones and iPads claim Mac OS X, and Android claims
// Linux. 'Edg' covers every Edge: Edg/ on desktop, EdgA/ on Android and EdgiOS/
// on iOS.
const BROWSERS = [
  ['Edg', 'Edge'],
  ['OPR/', 'Opera'],
  ['FxiOS/', 'Firefox'],
  ['Firefox/', 'Firefox'],
  ['CriOS/', 'Chrome'],
  ['Chrome/', 'Chrome'],
  ['Safari/', 'Safari'],
];
const SYSTEMS = [
  ['iPhone', 'iPhone'],
  ['iPad', 'iPad'],
  ['Android', 'Android'],
  ['CrOS', 'ChromeOS'],
  ['Mac OS X', 'Mac'],
  ['Windows', 'Windows'],
  ['Linux', 'Linux'],
];

const findIn = (userAgent, table) =>
  table.find(([marker]) => userAgent.includes(marker))?.[1];

// The name the passkey is listed under, e.g. "Chrome on Mac", so the list says
// where each one was added.
export function browserDeviceName(userAgent = globalThis.navigator?.userAgent) {
  const browser = findIn(userAgent || '', BROWSERS);
  const system = findIn(userAgent || '', SYSTEMS);
  if (browser && system) {
    return `${browser} on ${system}`;
  }
  return browser || system || 'Web browser';
}

// Asks the browser's password manager to drop a passkey the Halo API doesn't
// have. Best effort: only browsers with the WebAuthn Signal API can, and
// nothing is lost if one can't.
async function forgetUnregisteredPasskey(rpId, credentialId) {
  const signal = globalThis.PublicKeyCredential?.signalUnknownCredential;
  if (!signal || !rpId) {
    return;
  }
  try {
    await signal.call(globalThis.PublicKeyCredential, { rpId, credentialId });
  } catch {
    // See above.
  }
}

// Creates a passkey in this browser (or the password manager it uses) for the
// signed-in user and registers it with the Halo API.
export async function addPasskey() {
  const optionsJSON = await passkeyApi.getRegistrationOptions();
  const credential = await startRegistration({ optionsJSON });
  try {
    await passkeyApi.registerPasskey(
      passkeyName(browserDeviceName(), 'Web browser'),
      credential,
    );
  } catch (error) {
    // The passkey is already in the password manager. If the server refused
    // it, it would be offered at every passkey sign-in and always refused, so
    // drop it, as the app does. Not when the server may have saved it after
    // all (a timeout, say): that would leave a passkey the account lists but
    // this browser no longer has.
    if (registrationRefused(error)) {
      await forgetUnregisteredPasskey(optionsJSON.rp?.id, credential.id);
    }
    throw error;
  }
}

// Signs a Cognito passkey challenge (its WebAuthn request options) with a
// passkey the user picks in the browser's prompt.
export const answerPasskeyChallenge = (optionsJSON) =>
  startAuthentication({ optionsJSON });
